<svelte:options tag={null} />

<script lang="typescript">
  import { 
    onMount, 
    get_current_component 
  } from "svelte/internal";
  import "@everymatrix/retail-modal/dist/retail-modal";
  import "@everymatrix/em-card/dist/em-card";
  import { isPermissionPresent } from '@everymatrix/retail-helpers';

  export let src: string;
  export let playerid: string;

  let authHeaders: Headers;
  const thisComponent = get_current_component();
  let isLoading: boolean = false;
  let playerEmail: string;
  let resetSuccess: boolean = false;
  let resetError: boolean = false;
  let modalConfirm = false;
  let modalSuccess = false;
  let permissions;
  permissions = [];

  interface SecurityInfo {
    personalId: string;
    tfa?: string;
    securityQuestion: string;
    securityAnswer: string;
  }

  let securityInfo: SecurityInfo;

  const fetchSecurityInfo = async (src: string, headers: Headers) => {
    try {
      isLoading = true;
      const res = await fetch(src, { headers: { ...headers } });
      const data = await res.json();
      isLoading = false;
      if (res.ok) {
        securityInfo = {
          personalId: data.personalId,
          securityQuestion: data.securityQuestion,
          securityAnswer: data.securityAnswer,
        };
        securityInfo = { ...securityInfo };
        playerEmail = data.email;
      }
    } catch (err) {
      isLoading = false;
      throw new Error(`err:${err}`);
    }
  };

  const dispatchWcEvent = (name, detail) => {
    thisComponent.dispatchEvent(
      new CustomEvent(name, {
        detail,
        composed: true,
      })
    );
  };

  const handleResetPassword = async (src: string, headers: Headers) => {
    try {
      const res = await fetch(
        `${src}/resetpassword?email=${encodeURIComponent(playerEmail)}`,
        {
          method: "POST",
          headers: { ...headers },
        }
      );
      if (res.ok) {
        resetSuccess = true;
        resetError = false;
        dispatchWcEvent("change", "resetPassword");
      } else {
        resetError = true;
        resetSuccess = false;
      }
    } catch (err) {
      throw new Error(`err:${err}`);
    }
  };

  const initModal = () => {
    window.postMessage({ type: "ResetPasswordModal" }, "*");
    modalConfirm = true;
  };

  const handleEvents = (e) => {
    e.data?.type === "SetAuthHeaders" && 
      JSON.stringify(authHeaders) !== JSON.stringify(e.data.payload) && 
      (authHeaders = { ...e.data.payload })
    ;

    e.data?.type === "ResetPasswordModalConfirm" &&
      handleResetPassword(src, { ...authHeaders })
    ;

    if (e.data?.type === "ResetPasswordModalConfirm") {
      window.postMessage({ type: "ResetPasswordSuccessModal" }, "*");
      modalSuccess = true;
    };
  };

  onMount(() => {
    window.postMessage({ type: "GetAuthHeaders" }, "*");
    window.addEventListener("message", handleEvents);

    return () => {
      window.removeEventListener("message", handleEvents);
    }
  });

  const getPemissions = async () => {
    [ permissions.canResetPassword, 
      permissions.canSeeQuestion, 
      permissions.canSeeAnswer] = await Promise.all([
        isPermissionPresent(src, 'upp.dashboard.security-info.reset-password', 'WRITE'), 
        isPermissionPresent(src, 'upp.dashboard.security-info.question', 'READ'), 
        isPermissionPresent(src, 'upp.dashboard.security-info.answer', 'READ')
      ]);
  }

  $: src && getPemissions();
  $: src &&
    playerid &&
    authHeaders &&
    fetchSecurityInfo(`${src}${playerid}/profile`, { ...authHeaders });
</script>


<em-card>
  <h3 slot="header">
    Security Info
  </h3>

  <div slot="content" class="content">
    {#if securityInfo}
      <ul>
        <li>
          <strong>Personal ID:</strong>
          <span>
            {securityInfo.personalId}
          </span>
        </li>
        <li>
          <strong>Two Factor Auth:</strong>
          <span>
            {securityInfo.tfa || " - "}
          </span>
        </li>
        <li>
          <strong>Security Question:</strong>
          {#if permissions.canSeeQuestion}
            <span>
              {securityInfo.securityQuestion}
            </span>
          {:else}
            <span>***</span>
          {/if}
        </li>
        <li>
          <strong>Security Answer:</strong>
          {#if permissions.canSeeAnswer}
            <span>
              {securityInfo.securityAnswer}
            </span>
          {:else}
            <span>***</span>
          {/if}
        </li>
      </ul>
    {:else if isLoading}
      <ul class="loader">
        <li></li>
        <li></li>
        <li></li>
        <li></li>
        <li></li>
      </ul>
    {:else}
      <ul>
        <li>
          <p style="color: #cc2127;">Failed to fetch security info</p>
        </li>
      </ul>
    {/if}
    {#if permissions.canResetPassword}
      <div class="actions">
        <button on:click|preventDefault={initModal}>Reset Password</button>
      </div>
    {/if}
  </div>
</em-card>


{#if modalConfirm}
  <retail-modal
    modalname="ResetPasswordModal"
    modaltype="confirm"
    hasclose="false"
  >
    <h2 class="modal__title">Are you sure you want to reset your password?</h2>
  </retail-modal>
{/if}

{#if modalSuccess}
  <retail-modal modalname="ResetPasswordSuccessModal">
    <h2 class="modal__title">Reset password email has been sent!</h2>
  </retail-modal>
{/if}

<style lang="scss">
  *,
  *::before,
  *::after {
    margin: 0;
    padding: 0;
    list-style: none;
    text-decoration: none;
    outline: none;
    box-sizing: border-box;
    font-family: "Helvetica Neue", "Helvetica", sans-serif;
  }
  .content {
    display: flex;
    flex-direction: column;
  }

  .actions {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    margin-top: 20px;

    button {
      margin-bottom: 15px;
      min-height: 30px;
      padding: 6px 7px;
      color: #fff;
      background: #307fe2;
      border: 1px solid #307fe2;
      border-radius: 3px;
      font-size: 12px;
      font-weight: 700;
      text-transform: uppercase;
      line-height: 16px;
      &:hover {
        background: #000;
        border: 1px solid #000;
      }

      &:last-child { margin-bottom: 0; }
    }
  }

  li {
    display: grid;
    grid-template-columns: auto 1fr;
    margin-bottom: 10px;
    &:last-child {
      margin-bottom: 0;
    }

    strong { margin-right: 10px; }

    span { align-self: flex-end; }
  }
  .loader {
    min-width: 200px;

    li {
      position: relative;
      height: 18px;
      margin-bottom: 10px;
      animation-duration: 1.25s;
      animation-fill-mode: forwards;
      animation-iteration-count: infinite;
      animation-name: shimmer;
      animation-timing-function: linear;
      background: #f6f6f6;
      background: linear-gradient(
        to right,
        #f6f6f6 8%,
        #f0f0f0 18%,
        #f5f5f5 33%
      );
      background-size: 800px 104px;
      &:last-child {
        margin-bottom: 0;
      }
    }
  }
  // Animation
  @keyframes shimmer {
    0% {
      background-position: -468px 0;
    }
    100% {
      background-position: 468px 0;
    }
  }
</style>
